"use client";

import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import type { MapPin } from "@/design-data/map-atlas-data";

/**
 * An atlas embedded in a page — but only on screens big enough to be worth
 * it. Shared by the Destinations hero and the homepage map band.
 *
 * On a phone the full atlas plots ~770 pins as DOM nodes, which measured 771ms
 * of blocking work even on a desktop CPU, and it sits exactly where a thumb
 * lands first. So phones get a still card that opens /maps, where the map has
 * the whole screen and one finger pans it freely. Nothing is lost: the same
 * places are listed in the grid below, and every pin is on /maps.
 *
 * The breakpoint matches the Destinations hero's two-column switch, and is
 * the same line the phone discovery bar uses (globals.css).
 */
const MapAtlasClient = dynamic(() => import("@/components/MapAtlas").then((m) => m.MapAtlas), {
  ssr: false,
  loading: () => <MapCard interactive={false} />,
});

export const ATLAS_MIN_WIDTH = 1024;

export function MapCard({ interactive }: { interactive: boolean }) {
  const body = (
    <>
      <Image
        src="/images/hunza-valley-rakaposhi.webp"
        alt=""
        fill
        sizes="(max-width:1023px) 100vw, 50vw"
        className="object-cover opacity-55"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[rgba(0,0,0,0.85)] via-[rgba(0,0,0,0.35)] to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-5">
        <p className="m-0 text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--cta)]">
          The interactive atlas
        </p>
        <p className="mt-1.5 mb-0 font-[family-name:var(--font-display)] text-[1.3rem] font-medium text-white">
          Open the Map of Pakistan
        </p>
        <p className="mt-1 mb-0 text-[13px] text-[rgba(255,255,255,0.75)]">
          Every resort, sight and trail, full screen
        </p>
      </div>
    </>
  );

  const shell =
    "relative h-[240px] overflow-hidden rounded-[10px] border border-[var(--border-subtle)] bg-[var(--surface)]";

  // While the real map loads on a large screen this is only a placeholder, so
  // it must not be a link the visitor can click by mistake.
  return interactive ? (
    <Link href="/maps" className={`${shell} block no-underline`}>
      {body}
    </Link>
  ) : (
    <div className={shell} aria-hidden>
      {body}
    </div>
  );
}

export function MapEntryAtlas({ extraSightPins }: { extraSightPins?: MapPin[] }) {
  // Starts false so the server and the first client render agree; a large
  // screen swaps in the real map right after hydration.
  const [big, setBig] = useState(false);
  useEffect(() => {
    const query = window.matchMedia(`(min-width: ${ATLAS_MIN_WIDTH}px)`);
    const sync = () => setBig(query.matches);
    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);

  if (!big) return <MapCard interactive />;
  return <MapAtlasClient preview extraSightPins={extraSightPins} />;
}
